import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchChatsWhereUserIsMember, removeNotification } from 'firebase/firebaseFunctions';
import { useCurrentUser } from '@guryou/redux';
import { Spinner, AddIcon, CrossIcon } from '@guryou/html-components';
import { Icon } from '../../components/shared';

const SideBarChat = ({
  chatActive,
  chats,
  setActiveChat,
  providers,
  changeProvider,
  currentProvider,
  setLoadingMessage,
  loadingChats,
  users,
  currentTab,
  setCurrentTab,
  hasUnreadChats,
  hasUnreadChannels,
}) => {
  const { t } = useTranslation();
  const [newChatModal, setNewChatModal] = useState(false);
  const [loadingNewChat, setLoadingNewChat] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchUsers, setSearchUsers] = useState(users);

  const { user } = useCurrentUser();

  function searchChat(search) {
    setSearchTerm(search);

    if (search === '') {
      setSearchUsers(users);
    } else {
      const lowerSearch = search.toLowerCase();

      const filtered = users.filter(
        user => users.name?.toLowerCase().includes(lowerSearch) || user?.user?.email?.toLowerCase().includes(lowerSearch)
      );

      setSearchUsers(filtered);
    }
  }

  async function handleChangeActiveChat(chat) {
    try {
      setLoadingNewChat(true);
      setLoadingMessage(true);
      const unsubscribe = await fetchChatsWhereUserIsMember([chat.id, user.id], currentProvider.id);
      setLoadingMessage(false);
      await removeNotification(user.id, unsubscribe.id);
      setNewChatModal(false);
      setActiveChat({ ...chat, id: unsubscribe.id, receiveId: chat.id });
      setLoadingNewChat(false);
    } catch (error) {}
  }

  useEffect(() => {
    if (searchTerm === '') {
      setSearchUsers(users);
    }
  }, [users, searchTerm]);

  return (
    <div class="w-full h-full border-r border-gray-200 bg-white relative flex flex-col">
      {/* Header with back button */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-gray-50">
        <button
          onClick={() => changeProvider(null)}
          className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 transition-colors"
        >
          <Icon name="arrow-left" size="sm" />
          <div className="flex items-center gap-2">
            <img
              className="w-8 h-8 rounded-full ring-2 ring-gray-200"
              src={currentProvider?.images?.[0]?.small || '/icons/icon-512x512.png'}
              alt=""
            />
            <span className="text-sm font-semibold text-gray-900">{currentProvider?.name}</span>
          </div>
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 bg-white">
        <button
          onClick={() => setCurrentTab('chat')}
          className={`flex-1 flex items-center justify-center gap-2 p-3 text-sm font-semibold transition-all relative ${
            currentTab === 'chat' ? 'text-[var(--feel-primary)]' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <div className="relative">
            <Icon name="chat" size="sm" />
            {hasUnreadChats && currentTab !== 'chat' && (
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full border border-white"></span>
            )}
          </div>
          {t('CHAT.chat')}
          {currentTab === 'chat' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--feel-primary)]"></span>}
        </button>
        <button
          onClick={() => setCurrentTab('channel')}
          className={`flex-1 flex items-center justify-center gap-2 p-3 text-sm font-semibold transition-all relative ${
            currentTab === 'channel' ? 'text-[var(--feel-primary)]' : 'text-gray-500 hover:text-gray-700'
          }`}
        >
          <div className="relative">
            <Icon name="hashtag" size="sm" />
            {hasUnreadChannels && currentTab !== 'channel' && (
              <span className="absolute -top-1 -right-1 w-2 h-2 bg-red-500 rounded-full border border-white"></span>
            )}
          </div>
          {t('CHAT.channel')}
          {currentTab === 'channel' && <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-[var(--feel-primary)]"></span>}
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3 pb-24">
        {loadingChats && (
          <div class="flex items-center justify-center py-8">
            <div role="status">
              <Spinner small />
              <span class="sr-only">{t('CHAT.loading')}</span>
            </div>
          </div>
        )}
        {newChatModal && (
          <div className="fixed z-50 top-0 left-0 w-full h-full flex items-center justify-center bg-gray-900/50 p-4 backdrop-blur-sm">
            <div class="relative w-full max-w-2xl">
              {loadingNewChat && (
                <div class="flex items-center justify-center bg-white rounded-lg p-8">
                  <div role="status">
                    <Spinner small />
                    <span class="sr-only">{t('CHAT.loading')}</span>
                  </div>
                </div>
              )}
              {!loadingNewChat && (
                <div class="relative bg-white rounded-lg shadow-xl max-h-[80vh] flex flex-col">
                  <div class="flex items-center justify-between p-5 border-b border-gray-200">
                    <h3 class="text-xl font-semibold text-gray-900">{t('CHAT.newChat')}</h3>
                    <button
                      onClick={() => setNewChatModal(false)}
                      type="button"
                      class="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm w-8 h-8 inline-flex justify-center items-center transition-colors"
                    >
                      <CrossIcon />
                    </button>
                  </div>
                  <div class="p-5 space-y-4 overflow-y-auto flex-1">
                    <div class="relative">
                      <div class="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none">
                        <Icon name="search" className="text-gray-500" size="sm" />
                      </div>
                      <input
                        type="search"
                        class="block w-full p-3 pl-10 text-sm text-gray-900 border border-gray-300 rounded-lg bg-gray-50 focus:ring-[var(--feel-primary)] focus:border-[var(--feel-primary)]"
                        placeholder={t('CHAT.searchChats')}
                        onChange={e => searchChat(e.target.value)}
                        value={searchTerm}
                      />
                    </div>
                    {searchUsers?.map(chat => {
                      return (
                        <button
                          key={chat?.id}
                          class="flex items-center justify-between gap-4 p-3 w-full rounded-lg hover:bg-gray-100 transition-colors border border-transparent hover:border-gray-200"
                          onClick={() => handleChangeActiveChat(chat)}
                        >
                          <div class="flex items-center gap-3">
                            <img class="w-10 h-10 rounded-full ring-2 ring-gray-200" src={chat?.user?.image} alt="" />
                            <div class="font-medium flex flex-col items-start">
                              <div class="text-sm font-semibold text-gray-900">{chat?.name}</div>
                              <div class="text-xs text-gray-500">{chat?.user?.email}</div>
                            </div>
                          </div>
                          {chat?.unreadChats && <span class="w-2.5 h-2.5 bg-[var(--feel-primary)] rounded-full"></span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
        {!loadingChats && chats.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <div className="w-16 h-16 mb-4 rounded-full bg-gray-100 flex items-center justify-center">
              <Icon name="chat" size="lg" className="text-gray-400" />
            </div>
            <p className="text-sm font-medium text-gray-500">{t('CHAT.noChatsYet')}</p>
            <p className="text-xs text-gray-400 mt-1">{t('CHAT.startConversation')}</p>
          </div>
        )}
        {chats.map(chat => {
          return (
            <button
              class={`flex items-center justify-between gap-3 p-3 w-full rounded-lg hover:bg-gray-100 transition-colors border border-transparent hover:border-gray-200 ${
                chatActive?.receiveId === chat?.id ? 'bg-gray-100 border-gray-200 border' : ''
              } `}
              key={chat?.id}
              onClick={() => handleChangeActiveChat(chat)}
            >
              <div class="flex items-center gap-3 flex-1 min-w-0">
                <div className="relative">
                  <img class="w-10 h-10 rounded-full ring-2 ring-gray-200" src={chat?.user?.image} alt="" />
                  {chat?.unreadChats && (
                    <span class="absolute -top-1 -right-1 w-3 h-3 bg-[var(--feel-primary)] border-2 border-white rounded-full"></span>
                  )}
                </div>

                <div class="font-medium flex flex-col items-start flex-1 min-w-0">
                  <div class="text-sm font-semibold text-gray-900 truncate w-full text-left">{chat?.name}</div>
                  <div class="text-xs text-gray-500 truncate w-full text-left">{chat?.user?.email}</div>
                </div>
              </div>
              <Icon name="chevron-right" className="text-gray-400" size="md" />
            </button>
          );
        })}
      </div>

      <div className="sticky bottom-0 p-4 bg-gradient-to-t from-white via-white to-transparent pointer-events-none">
        <button
          onClick={() => setNewChatModal(true)}
          type="button"
          className="w-full flex items-center justify-center gap-2 text-white font-medium rounded-lg text-sm px-5 py-2.5 shadow-lg hover:shadow-xl focus:ring-4 focus:outline-none focus:ring-[var(--feel-primary)]/50 transition-all transform hover:scale-105 pointer-events-auto"
          style={{ backgroundColor: 'var(--feel-primary)' }}
          onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = 'color-mix(in srgb, var(--feel-primary) 85%, black)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = 'var(--feel-primary)';
          }}
        >
          <AddIcon /> {t('CHAT.newChat')}
        </button>
      </div>
    </div>
  );
};

export default SideBarChat;
